import { Availabilities, TimeSlots } from '@customTypes/Availabilities'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import {
  groupAvailabilitiesByWeeks,
  GroupedAvailabilityMonth,
} from 'src/utils/groupAvailabilitiesByWeeks/groupAvailabilitiesByWeeks'
import { getDisplayedDateRangeByDate } from 'src/utils/getDisplayedDateRangeByDate/getDisplayedDateRangeByDate'
import { getDateWithDisplayedMonthAndYearByDate } from 'src/utils/getDateWithDisplayedMonthAndYearByDate/getDateWithDisplayedMonthAndYearByDate'
import { publicClient } from './apiClients'
import { getAvailabilitiesFromTimeSlots } from './mappers'
import { reportErrorToSentry } from 'src/utils/reportErrorToSentry'
import { add, endOfDay, isAfter } from 'date-fns'
import { cleanObjectFromEmptyKeys } from 'src/helpers/cleanObjectFromEmptyKeys'
import { InsuranceTypeOptions } from 'src/config/insuranceTypeOptions'
import queryString from 'query-string'
import { NearbyClinicsIdWithTreatmentBundleId } from '@hooks/useAvailabilities/helpers/useNearbyClinicIdsWithTreatmentBundles'

export interface GetAvailabilitiesProps {
  clinicReferenceId: string
  nearbyClinics?: NearbyClinicsIdWithTreatmentBundleId
  treatmentBundleReferenceId?: string
  treatmentReferenceId?: string
  practitionerReferenceIds?: string[]
  patientType?: PatientTypeOptions
  customerId?: string
  appointmentBundleReferenceId?: string
  patientReferenceId?: string
  insuranceType?: InsuranceTypeOptions
}

export type ClinicAvailabilities = Record<string, GroupedAvailabilityMonth>

export const getGroupedAvailabilities = async (
  props: GetAvailabilitiesProps,
  cursor?: [Date, Date]
): Promise<ClinicAvailabilities> => {
  const [startDate, endDate] = cursor || getDisplayedDateRangeByDate(getDateWithDisplayedMonthAndYearByDate(new Date()))
  const start = startDate.toISOString()
  const end = endDate.toISOString()

  const fetchAvailabilities = async (clinicReferenceId: string, treatmentBundleReferenceId: string | undefined) => {
    return getAvailabilities({
      clinicReferenceId,
      patientType: props.patientType,
      treatmentBundleReferenceId: treatmentBundleReferenceId || '',
      treatmentReferenceId: props.treatmentReferenceId || '',
      employeeReferenceIds: props.practitionerReferenceIds,
      appointmentBundleReferenceId: props.appointmentBundleReferenceId,
      patientReferenceId: props.patientReferenceId,
      insuranceType: props.insuranceType,
      start,
      end,
    })
  }

  if (props.nearbyClinics?.length) {
    const availabilitiesResponses = await Promise.all(
      props.nearbyClinics.map((clinic) => fetchAvailabilities(clinic.clinicId, clinic.treatmentBundleReferenceId))
    )
    const groupedAvailabilities = availabilitiesResponses.map((res) =>
      groupAvailabilitiesByWeeks(res, new Date(start), endOfDay(new Date(end)))
    )

    return props.nearbyClinics.reduce(
      (prev, clinic, index) => ({ ...prev, [clinic.clinicId]: groupedAvailabilities[index] }),
      {} as ClinicAvailabilities
    )
  }

  const availabilities = await fetchAvailabilities(props.clinicReferenceId, props.treatmentBundleReferenceId)

  return {
    [props.clinicReferenceId]: groupAvailabilitiesByWeeks(availabilities, new Date(start), endOfDay(new Date(end))),
  }
}

interface GetAvailabilitiesToValidateSelectedSlotArguments {
  clinicReferenceId: string
  treatmentReferenceId: string
  practitionerReferenceIds: string[] | undefined
  patientType: PatientTypeOptions
  start?: string
  end?: string
  customerId?: string
  insuranceType?: InsuranceTypeOptions
}

export interface GetAvailabilitiesParams {
  clinicReferenceId?: string
  treatmentBundleReferenceId: string
  treatmentReferenceId?: string
  patientType?: PatientTypeOptions
  start: string
  end: string
  employeeReferenceIds?: string[]
  appointmentBundleReferenceId?: string
  patientReferenceId?: string
  insuranceType?: InsuranceTypeOptions
  maxSlots?: number
  maxDaysWithSlots?: number
}

export const getAvailabilities = async (params: GetAvailabilitiesParams): Promise<Availabilities[]> => {
  const { employeeReferenceIds, ...restParams } = params

  const startDate = new Date(restParams.start)
  const endDate = new Date(restParams.end)
  const { creationEvent } = queryString.parse(window.location.search)

  if (!startDate) {
    reportErrorToSentry('Trying to fetch TimeSlots without start date')
  }
  if (!endDate) {
    reportErrorToSentry('Trying to fetch TimeSlots without end date')
  }
  if (!isAfter(endDate, startDate)) {
    reportErrorToSentry('end date is not after start date')
  }

  const availabilitiesParams = {
    ...restParams,
    employeeReferenceIds: employeeReferenceIds?.join(','),
    creationEvent,
  }

  const { data } = await publicClient.get<TimeSlots[]>(`/time-slots`, {
    params: cleanObjectFromEmptyKeys(availabilitiesParams),
  })

  return getAvailabilitiesFromTimeSlots(data)
}

export const getAvailabilitiesToValidateSelectedSlot = async ({
  clinicReferenceId,
  treatmentReferenceId,
  practitionerReferenceIds,
  patientType,
  start,
  end,
  insuranceType,
}: GetAvailabilitiesToValidateSelectedSlotArguments): Promise<Availabilities[]> => {
  const startDate = start || new Date().toISOString()
  let endDate = end || ''
  const isEndDateAfterStartDate = isAfter(new Date(endDate), new Date(startDate))

  if (!isEndDateAfterStartDate) {
    endDate = add(new Date(startDate), { days: 1 }).toISOString()
  }

  const availabilities = await getAvailabilities({
    clinicReferenceId,
    patientType,
    treatmentBundleReferenceId: treatmentReferenceId,
    employeeReferenceIds: practitionerReferenceIds,
    start: startDate,
    end: isEndDateAfterStartDate ? endDate : startDate,
    insuranceType,
  })

  return availabilities
}
